import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Heart, MessageCircle, PenLine, ArrowLeft, Clock } from "lucide-react";
import api, { API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate, timeAgo } from "@/lib/format";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import JournalContent from "@/components/journal/JournalContent";
import { Avatar, StatusPill } from "@/components/blog/BlogBits";
import { LoadError, isNotFound } from "@/components/LoadError";

function Comments({ post, user }) {
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const { data } = useQuery({ queryKey: ["blog-comments", post.id], queryFn: async () => (await api.get(`/blog/post/${post.id}/comments`)).data });
  const comments = data?.comments || [];
  const pending = data?.pending_mine || [];
  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data: c } = await api.post(`/blog/post/${post.id}/comments`, { text: text.trim() });
      toast.success(c.status === "published" ? "Comment posted." : "Thanks! Your comment will appear once a moderator approves it.");
      setText("");
      qc.invalidateQueries({ queryKey: ["blog-comments", post.id] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <section className="mt-14" data-testid="blog-comments">
      <h2 className="flex items-center gap-2 font-serif text-2xl font-semibold text-foreground"><MessageCircle className="h-5 w-5 text-gold" /> Conversation <span className="font-mono text-sm text-muted-foreground">({comments.length})</span></h2>
      <ul className="mt-6 space-y-5">
        {comments.map((c) => (
          <li key={c.id} className="flex gap-3" data-testid={`comment-${c.id}`}>
            <Avatar author={c.author} />
            <div className="min-w-0 flex-1 rounded-2xl bg-muted/50 px-4 py-3">
              <p className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground"><Link to={`/blog/author/${c.author.user_id}`} className="font-semibold text-foreground hover:text-gold">{c.author.name}</Link> · {timeAgo(c.created_at)}</p>
              <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-foreground">{c.text}</p>
            </div>
          </li>
        ))}
        {pending.map((c) => (
          <li key={c.id} className="flex gap-3 opacity-70" data-testid={`comment-pending-${c.id}`}>
            <Avatar author={c.author} />
            <div className="min-w-0 flex-1 rounded-2xl border border-dashed border-amber-400/60 px-4 py-3">
              <p className="text-xs text-amber-700">Awaiting moderation — only you can see this yet</p>
              <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-foreground">{c.text}</p>
            </div>
          </li>
        ))}
        {!comments.length && !pending.length && <li className="text-sm text-muted-foreground" data-testid="comments-empty">No comments yet. Start the conversation.</li>}
      </ul>
      {user ? (
        <div className="mt-6 rounded-2xl border border-border p-4">
          <Textarea value={text} onChange={(e) => setText(e.target.value)} maxLength={2000} rows={3} placeholder="Add your thoughts… (comments are reviewed before they appear)" data-testid="comment-input" />
          <div className="mt-3 flex justify-end"><Button onClick={send} disabled={busy || !text.trim()} className="rounded-full" data-testid="comment-submit">Post comment</Button></div>
        </div>
      ) : (
        <p className="mt-6 text-sm text-muted-foreground"><Link to="/blog/write" className="text-gold hover:underline">Sign in</Link> to join the conversation.</p>
      )}
    </section>
  );
}

export default function BlogPost() {
  const { slug } = useParams();
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data: post, isLoading, isError, error, refetch } = useQuery({ queryKey: ["blog-post", slug], queryFn: async () => (await api.get(`/blog/post/${slug}`)).data });

  const amen = async () => {
    if (!user) { toast("Sign in to say Amen."); return; }
    try {
      const { data } = await api.post(`/blog/post/${post.id}/amen`);
      qc.setQueryData(["blog-post", slug], (p) => ({ ...p, amens: data.amens, amen_mine: data.mine }));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (isLoading) return <div className="mx-auto max-w-3xl px-4 py-16"><div className="h-96 animate-pulse rounded-2xl bg-muted" /></div>;
  if (isError && !isNotFound(error)) return <LoadError onRetry={refetch} />;
  if (isError || !post) return <div className="mx-auto max-w-3xl px-4 py-24 text-center" data-testid="blog-post-missing"><p className="font-serif text-3xl text-foreground">We couldn't find that post.</p><Link to="/blog" className="mt-4 inline-block text-gold hover:underline">Back to the blog</Link></div>;

  const mine = user && (user.user_id === post.author.user_id || user.role === "admin");
  return (
    <article className="mx-auto max-w-3xl px-4 py-10 sm:px-6" data-testid="blog-post">
      <Seo title={`${post.title} — QuestionGod Blog`} description={post.excerpt} image={post.cover_image_id ? `${API}/blog/images/${post.cover_image_id}` : undefined} type="article" />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="blog-back"><ArrowLeft className="h-4 w-4" /> All posts</Link>
        {mine && <div className="flex items-center gap-2">{post.status !== "published" && <StatusPill status={post.status} />}<Link to={`/blog/edit/${post.id}`} className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs font-semibold hover:bg-muted" data-testid="blog-edit-link"><PenLine className="h-3.5 w-3.5" /> Edit</Link></div>}
      </div>
      {post.status === "rejected" && post.rejection_note && <p className="mt-4 rounded-2xl border border-red-300 bg-red-50 p-4 text-sm text-red-800" data-testid="blog-rejection-note"><span className="font-semibold">Moderator note:</span> {post.rejection_note}</p>}
      <header className="mt-6">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-gold">{post.category}</p>
        <h1 className="mt-3 font-serif text-4xl font-bold leading-[1.1] tracking-tight text-foreground sm:text-5xl">{post.title}</h1>
        <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-muted-foreground">
          <Link to={`/blog/author/${post.author.user_id}`} className="flex items-center gap-2 hover:text-foreground" data-testid="blog-author-link"><Avatar author={post.author} size="h-9 w-9" /><span className="font-semibold text-foreground">{post.author.name}</span></Link>
          {post.published_at && <span>{fmtDate(post.published_at)}</span>}
          <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {post.read_minutes} min read</span>
        </div>
      </header>
      {post.cover_image_id && <img src={`${API}/blog/images/${post.cover_image_id}`} alt="" className="mt-8 max-h-[60vh] w-full rounded-3xl object-cover" data-testid="blog-cover" />}
      <JournalContent doc={post.doc} className="blog-prose mt-10 text-[17px] leading-relaxed" testId="blog-content" />
      <div className="mt-10 flex flex-wrap items-center gap-4 border-y border-border py-5">
        <button type="button" onClick={amen} className={`inline-flex items-center gap-2 rounded-full border px-5 py-2 text-sm font-semibold transition-colors ${post.amen_mine ? "border-gold bg-gold/15 text-gold" : "border-border text-foreground hover:bg-muted"}`} data-testid="blog-amen-button">
          <Heart className={`h-4 w-4 ${post.amen_mine ? "fill-current" : ""}`} /> Amen <span className="font-mono text-xs" data-testid="blog-amen-count">{post.amens}</span>
        </button>
        <p className="text-sm text-muted-foreground">Did this stir something in you? Say Amen, or add to the conversation below.</p>
      </div>
      {post.author.bio && (
        <aside className="mt-8 flex gap-4 rounded-2xl bg-muted/40 p-5" data-testid="blog-author-bio">
          <Avatar author={post.author} size="h-12 w-12" />
          <div><p className="font-semibold text-foreground">{post.author.name}</p><p className="mt-1 text-sm leading-relaxed text-muted-foreground">{post.author.bio}</p><Link to={`/blog/author/${post.author.user_id}`} className="mt-2 inline-block text-xs text-gold hover:underline">More from {post.author.name}</Link></div>
        </aside>
      )}
      {post.status === "published" && <Comments post={post} user={user} />}
    </article>
  );
}
